import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, Database, Mail, Globe, Megaphone } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import CrmMockup from "@/components/CrmMockup";
import AiChatMockup from "@/components/AiChatMockup";

function AdsVisual() {
  return (
    <div className="rounded-2xl border border-white/10 bg-ink2 p-6 shadow-2xl">
      <div className="flex items-center gap-2.5 border-b border-white/10 pb-4">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-dblue to-electric"><Megaphone className="h-4 w-4 text-white" /></span>
        <p className="font-display text-sm font-bold">META ADS — B2B CAMPAIGN</p>
      </div>
      <div className="mt-5 space-y-4">
        {["B2B Leads", "Quote requests", "Partnerships"].map((label, i) => (
          <div key={label}>
            <div className="flex justify-between text-[11px] text-steel mb-1.5">
              <span>{label}</span>
              <span className="text-electric font-bold">{[52, 31, 18][i]}</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5">
              <div className="h-full rounded-full bg-gradient-to-r from-dblue to-electric" style={{ width: `${[86, 64, 92][i]}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WebVisual() {
  return (
    <div className="grid grid-cols-2 gap-4">
      {["GR", "EN"].map((l, i) => (
        <div key={l} className={`rounded-xl border border-white/10 bg-ink2 shadow-xl overflow-hidden ${i === 1 ? "mt-8" : ""}`}>
          <div className="flex items-center gap-1.5 border-b border-white/10 px-3 py-2">
            <span className="h-2 w-2 rounded-full bg-white/20" /><span className="h-2 w-2 rounded-full bg-white/20" />
            <span className="ml-auto rounded bg-gradient-to-r from-dblue to-electric px-2 py-0.5 text-[9px] font-bold text-white">{l}</span>
          </div>
          <div className="space-y-2.5 p-4">
            <div className="h-2.5 w-2/3 rounded bg-white/70" />
            <div className="h-1.5 w-full rounded bg-white/15" />
            <div className="h-1.5 w-5/6 rounded bg-white/15" />
            <div className="h-1.5 w-4/6 rounded bg-white/15" />
            <div className="mt-3 h-7 w-24 rounded-full bg-gradient-to-r from-dblue to-electric" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmailVisual({ examples }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-ink2 p-8">
      <div className="space-y-3">
        {examples.map((e) => (
          <div key={e} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-5 py-4">
            <Mail className="h-4 w-4 text-electric" />
            <span className="font-mono text-sm text-white/90">{e}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ServicePage({ id }) {
  const { t, link } = useLang();
  const s = t(`spages.${id}`);

  const visuals = {
    "b2b-advertising": <AdsVisual />,
    websites: <WebVisual />,
    crm: <CrmMockup />,
    "ai-secretary": <AiChatMockup />,
    "business-email": <EmailVisual examples={t("email.examples")} />,
  };

  return (
    <>
      <Seo page={id} />
      <section className="relative overflow-hidden bg-ink bg-grid-dark pt-[140px] pb-16 lg:pt-[180px] lg:pb-24">
        <div className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-dblue/20 blur-[140px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.25em] text-electric">{s.eyebrow}</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">{s.title}</h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-steel leading-relaxed">{s.lead}</p>
            <p className="mt-6 border-l-2 border-electric pl-4 font-display text-lg font-bold text-gradient max-w-xl">{s.message}</p>
            {s.message2 && <p className="mt-2 border-l-2 border-electric/40 pl-4 text-sm text-steel">{s.message2}</p>}
            <Link
              to={link("/contact")}
              data-testid="service-hero-cta"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-dblue to-electric px-7 py-4 text-sm font-semibold transition-transform duration-200 hover:scale-[1.04]"
            >
              {s.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-mist text-ink py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {s.blocks.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-ink/10 bg-white p-7">
                  <p className="font-display text-xs font-bold tracking-[0.2em] text-dblue">{String(i + 1).padStart(2, "0")}</p>
                  <h2 className="mt-4 font-display text-lg font-bold tracking-tight">{b.title}</h2>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-wrap gap-2.5">
              {s.bullets.map((b) => (
                <span key={b} className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2 text-xs font-medium text-slate-700">
                  <Check className="h-3.5 w-3.5 text-dblue" /> {b}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink2 bg-grid-dark py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{s.message2 || s.message}</h2>
            <div className="mt-6 space-y-3">
              {(id === "crm" ? t("crm.pipeline") : id === "ai-secretary" ? t("aicrm.steps") : s.bullets.slice(0, 4)).map((step, i, arr) => (
                <div key={step} className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-electric/30 bg-electric/10 text-[10px] font-bold text-electric">{i + 1}</span>
                  <span className="text-sm font-semibold text-white/90">{step}</span>
                  {i < arr.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-electric/50" />}
                </div>
              ))}
            </div>
            <Link
              to={link("/contact")}
              data-testid="service-cta-bottom"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-dblue to-electric px-7 py-3.5 text-sm font-semibold transition-transform duration-200 hover:scale-[1.04]"
            >
              {s.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>{visuals[id]}</Reveal>
        </div>
      </section>
    </>
  );
}
